import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { LayoutModeEnum, EnableModeEnum } from '../type/modeState.type';

export const useUserStore = create(
    devtools(
        persist(
            (set) => ({
                partnerId: null,
                partnerType: null,
                chatUserId: null,
                isMobileDevice: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
                
                setIsMobileDevice: (isMobileDevice) => set({ isMobileDevice }),
                setPartnerId: (partnerId) => set({ partnerId }),
                setPartnerType: (partnerType) => set({ partnerType }),
                setChatUserId: (chatUserId) => set({ chatUserId }),
            }),
            {
                name: 'user-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useChatbotStore = create(
    devtools(
        persist(
            (set) => ({
                chatbot: null,
                bootConfigGreetingComment: '',
                setChatbot: (chatbot) => set({ chatbot }),
                setBootConfigGreetingComment: (bootConfigGreetingComment) => set({ bootConfigGreetingComment }),
                clearBootConfigGreetingComment: () => set({ bootConfigGreetingComment: '' })
            }),
            {
                name: 'chatbot-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useChatStateStore = create(
    devtools(
        persist(
        set => ({
            isChatbotPending: false,
            isNewProduct: false,
            newProductCount: 0,
            currentItemId: [],
            currentShrinkCardIndex: 0,
            currentListCardIndex: 0,
            isPreAnswered: false,
            isCsFlowActive: false,
            isFormSubmitted: 'initial', // initial, ready, submitted
            isExceptional: false,
            isConsole: false,
            isStreaming: false,
            connectionId: null,
            displayLocation: null,
            displayItemId: null,
            isPDPChat: false,
            currentPage: null,
            enableMode: EnableModeEnum.DEFAULT,
            isChatOpen: false,
            language: 'ko',
            isInConsoleTour: false,
            customizedGreeting: null,
            layoutMode: LayoutModeEnum.MODAL,
            addToCartWithOption: false,  // FlowLift project: B variant - false, C variant - true, default is false
            isLastIntentProductRecommendation: false,   // one of the intents: '제공', '추천', '딥리서치'
            setIsChatbotPending: (isChatbotPending) => set({ isChatbotPending }),
            setIsNewProduct: (isNewProduct) => set({ isNewProduct }),
            setNewProductCount: (newProductCount) => set({ newProductCount }),
            setCurrentItemId: (currentItemId) => {
                set({ currentItemId })
            },
            setCurrentShrinkCardIndex: (currentShrinkCardIndex) => set({ currentShrinkCardIndex }),
            setCurrentListCardIndex: (currentListCardIndex) => set({ currentListCardIndex }),
            setIsPreAnswered: (isPreAnswered) => set({ isPreAnswered }),
            setIsFormSubmitted: (isFormSubmitted) => set({ isFormSubmitted }),
            setIsExceptional: (isExceptional) => set({ isExceptional }),
            setIsConsole: (isConsole) => set({ isConsole }),
            setIsStreaming: (isStreaming) => set({ isStreaming }),
            setConnectionId: (connectionId) => set({ connectionId }),
            setDisplayLocation: (displayLocation) => set({ displayLocation }),
            setDisplayItemId: (displayItemId) => set({ displayItemId }),
            setIsPDPChat: (isPDPChat) => set({ isPDPChat }),
            setCurrentPage: (currentPage) => set({ currentPage }),
            setEnableMode: (enableMode) => set({ enableMode }),
            setIsChatOpen: (isChatOpen) => set({ isChatOpen }),
            setLanguage: (language) => set({ language }),
            setIsInConsoleTour: (isInConsoleTour) => set({ isInConsoleTour }),
            setCustomizedGreeting: (customizedGreeting) => set({ customizedGreeting }),
            clearCustomizedGreeting: () => set({ customizedGreeting: null }),
            clearIsInConsoleTour: () => set({ isInConsoleTour: false }),
            clearCurrentShrinkCardIndex: () => set({ currentShrinkCardIndex: 0 }),
            clearCurrentListCardIndex: () => set({ currentListCardIndex: 0 }),
            setLayoutMode: (layoutMode) => set({ layoutMode }),
            setAddToCartWithOption: (addToCartWithOption) => set({ addToCartWithOption }),
            setIsLastIntentProductRecommendation: (isLastIntentProductRecommendation) => set({ isLastIntentProductRecommendation }),
            setIsCsFlowActive: (isCsFlowActive) => set({ isCsFlowActive }),
        }),
        {
            name: 'chat-state-storage',
            storage: createJSONStorage(() => sessionStorage),
        }
    )
)
);

export const useMessagesStore = create(
    devtools(
        persist(
        set => ({
            messages: [],
            streamMessage: '',
            isBuilder: false,
            guidelineId: null,
            guidelineContent: '',
            guidelineTitle: '',
            setMessages: (messages) => set((prev) => {
                const newMessages = Array.isArray(messages) ? messages : [messages];
                // 스켈레톤이면 대체하기
                if (prev.messages.length > 0 && prev.messages[prev.messages.length - 1].isLoading) {
                    return {
                        messages: [...prev.messages.slice(0, -1), ...newMessages]
                    }
                }
                return {
                    messages: [...prev.messages, ...newMessages]
                }
            }),
            clearMessages: () => set({ messages: [] }),
            setStreamMessage: (streamMessage) => set((prev) => {
                return {
                    streamMessage: prev.streamMessage + streamMessage
                }
            }),
            clearStreamMessage: () => set({ streamMessage: '' }),
            setIsBuilder: (isBuilder) => set({ isBuilder }),
            setGuidelineId: (guidelineId) => set({ guidelineId }),
            setGuidelineContent: (guidelineContent) => set({ guidelineContent }),
            setGuidelineTitle: (guidelineTitle) => set({ guidelineTitle }),
            clearGuidelineId: () => set({ guidelineId: null }),
            clearGuidelineContent: () => set({ guidelineContent: '' }),
            clearGuidelineTitle: () => set({ guidelineTitle: '' }),
        }),
        {
            name: 'messages-storage',
            storage: createJSONStorage(() => sessionStorage),
        }
        )
    )
);

export const useProductsStore = create(
    devtools(
        persist(
            (set) => ({
                products: [],
                examples: [],
                followupQuestion: [],
                structuredProducts: [],
                setProducts: (products) => set((prev) => {
                    return {
                        products: [...products, ...prev.products]
                    }
                }),
                setExamples: (examples) => set({ examples }),
                setFollowupQuestion: (followupQuestion) => set({ followupQuestion }),
                setStructuredProducts: (structuredProducts) => set((prev) => {
                    return {
                        structuredProducts: [...structuredProducts, ...prev.structuredProducts]
                    }
                }),
                clearProducts: () => set({ products: [] }),
                clearExamples: () => set({ examples: [] }),
                clearFollowupQuestion: () => set({ followupQuestion: [] }),
                clearStructuredProducts: () => set({ structuredProducts: [] }),
            }),
            {
                name: 'products-storage',
                storage: createJSONStorage(() => sessionStorage, {
                    replacer: (key, value) => {
                        // 직접 serialize
                        if (key === 'products' && Array.isArray(value)) {
                            
                            return value.map(product => ({
                                ...product,
                                variantList: Array.isArray(product.variantList) ? product.variantList : [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: Array.isArray(product.category) ? product.category : [],
                                currency: product.currency || 'KRW',
                                idx: Number(product.idx) || 0,
                            }));
                        }
                        
                        return value;
                    },
                    reviver: (key, value) => {
                        // 직접 deserialize
                        if (key === 'products' && Array.isArray(value)) {
                            
                            return value.map(product => ({
                                ...product,
                                variantList: Array.isArray(product.variantList) ? product.variantList : [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: Array.isArray(product.category) ? product.category : [],
                                currency: product.currency || 'KRW',
                                idx: Number(product.idx) || 0,
                            }));
                        }
                        
                        return value;
                    }
                }),
                merge: (persistedState, currentState) => {
                    // persistance 주의하여 직접 merge
                    const mergedState = { ...currentState, ...persistedState };
                    if (persistedState?.products && Array.isArray(persistedState.products)) {
                        mergedState.products = persistedState.products.map(product => {
                            return {
                                ...product,
                                variantList: product.variantList || [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: product.category || [],
                                currency: product.currency || 'KRW',
                                idx: product.idx ?? 0,
                            };
                        });
                    }
                    
                    return mergedState;
                },
            }
        )
    )
);

export const useFormStore = create(
    devtools(
        persist(
            (set) => ({
                formData: {},
                preAnswersObject: [],
                setFormData: (formData) => set({ formData }),
                setPreAnswersObject: (preAnswersObject) => set({ preAnswersObject }),
                clearPreAnswersObject: () => set({ preAnswersObject: [] }),
            }),
            {
                name: 'form-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useUtmStore = create(
    devtools(
        persist(
            set => ({
                utmParams: {},
                setUtmParams: (utmParams) => set({ utmParams }),
            }),
            {
                name: 'utm-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useToastStore = create(
    devtools(
        persist(
            (set) => ({
                toastMessage: '',
                isToastVisible: false,
                setToastMessage: (toastMessage) => set({ toastMessage }),
                setIsToastVisible: (isToastVisible) => set({ isToastVisible }),
            }),
            {
                name: 'toast-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);